import { useCallback, useState } from 'react';
import SysAdminUserFormComponent from '../components/sysadmin-user-form.component';
import { SysAdminUserCreate, SysAdminUserUpdate } from '../types/sysadmin-user.types';
import { useSysAdminUserCreate } from '../hooks/use-sysadmin-user.hook';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

const SysAdminUserCreateContainer = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  const { mutateAsync: createUser, isPending } = useSysAdminUserCreate();

  const onFormSubmit = useCallback(
    async (data: SysAdminUserCreate | SysAdminUserUpdate, resetForm: () => void) => {
      // Reset validation errors on new submission
      setValidationErrors([]);

      // For create operations, we know we'll have all required fields
      const createData = data as SysAdminUserCreate;
      try {
        const response = await createUser(createData);
        message.success(t('crud.create.success', { entity: t('users.title') }));
        resetForm();
        navigate(`/dashboard/admin/user/${response.data.id}`);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.create.error', { entity: t('users.title') })
        );
        message.error(errorMessage);
      }
    },
    [createUser, navigate, t]
  );

  return (
    <>
      <PageTitle title={t('crud.create.title', { entity: t('users.title') })} />
      <HeaderRow
        title={t('crud.create.title', { entity: t('users.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <SysAdminUserFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminUserCreateContainer, 'user.create');
